/* eslint-disable jsx-a11y/label-has-for */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import {
  APPLY_USERINPUT_TYPES,
  CHANNEL_TYPES,
  APPLY_ACTION_TYPES,
} from '@bingads-webui-campaign/recommendation-core';

import { Checkbox } from '@bingads-webui-react/checkbox';
import { HelpPopup } from '@bingads-webui-react/help-popup';

import { GridPanel } from './grid-panel';
import { userInputsMappingConfigForGrid } from './user-inputs-mapping-config';

const PLUS_CHARACTER = '+';
const formatIncreasedValue = (value, checker) => {
  if (checker && value[0] !== '-' && value[0] !== '0') {
    return `${PLUS_CHARACTER}${value}`;
  }
  return value;
};

export class GridActionWithEstimates extends React.PureComponent {
  static propTypes = {
    title: PropTypes.string.isRequired,
    headers: PropTypes.arrayOf(PropTypes.string).isRequired,
    bodies: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    hasLastRowAsCurrent: PropTypes.bool,
    singleSelection: PropTypes.bool,
    i18n: PropTypes.PropTypes.shape({
      getString: PropTypes.func.isRequired,
    }).isRequired,
    selectedRow: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    helpTopics: PropTypes.arrayOf(PropTypes.string),
    children: PropTypes.element,
    model: PropTypes.arrayOf(PropTypes.object).isRequired,
    track: PropTypes.func.isRequired,
    type: PropTypes.string.isRequired,
    target: PropTypes.objectOf(PropTypes.any).isRequired,
    rowClass: PropTypes.string.isRequired,
    logOpportunityEvent: PropTypes.func.isRequired,
    readOnly: PropTypes.bool.isRequired,
    context: PropTypes.shape({
      Level: PropTypes.string.isRequired,
    }).isRequired,
    guid: PropTypes.string.isRequired,
    logExpInfo: PropTypes.func.isRequired,
  };

  static defaultProps = {
    hasLastRowAsCurrent: false,
    singleSelection: false,
    helpTopics: [],
    children: null,
  };

  constructor(props) {
    super(props);

    _.each(props.model, ({
      opportunityId,
    }) => {
      props.logOpportunityEvent({
        opportunityId,
        campaignId: props.target.CampaignId,
        context: props.context,
      });
    });
  }

  state = {
    // this only applies to single selection grid.
    selectedRow: this.props.selectedRow,
    rowState: _.map(this.props.bodies, () => true),
  }

  getUserInputs(isApply, returnOnlyWhenModified = false) {
    let modified = false;

    const config = userInputsMappingConfigForGrid[this.props.type];
    const userInputs = [];
    if (this.props.singleSelection) {
      const selectedIndex = parseInt(this.state.selectedRow, 10);
      const selectedOpportunityModel = this.props.model[selectedIndex];
      const selectConfig = config.selected;

      modified = (this.state.selectedRow !== this.props.selectedRow);
      userInputs.push(_.extend({}, {
        OpportunityId: selectedOpportunityModel.opportunityId,
      }, isApply && modified && {
        [selectConfig.nameInRequest]: selectedOpportunityModel[selectConfig.property],
      }));
    } else {
      _.each(this.state.rowState, (checked, index) => {
        if (checked === true) {
          userInputs.push({
            OpportunityId: this.props.model[index].opportunityId,
          });
        } else {
          modified = true;
        }
      });
    }

    if (returnOnlyWhenModified && !modified) return null;

    const odataType = APPLY_USERINPUT_TYPES[this.props.type];

    if (isApply) {
      return _.map(userInputs, userInput => _.extend(userInput, odataType && {
        '@odata.type': odataType,
      }));
    }
    return userInputs;
  }

  handleRadioChange = (e) => {
    this.setState({
      selectedRow: e.target.value,
    });
  }

  handleCheckboxChange = (e) => {
    const rowIndex = parseInt(e.target.name, 10);

    if (rowIndex === -1) {
      this.setState({
        rowState: _.map(this.props.bodies, () => e.target.checked),
      });
    } else {
      this.setState(({ rowState }) => {
        const newState = [...rowState];
        newState[rowIndex] = !newState[rowIndex];

        return {
          rowState: newState,
        };
      });
    }
  }

  buildRequestOptions(isApply) {
    const campaignAdGroups = [{
      Key: this.props.target.CampaignId,
      Value: this.props.target.AdGroupId ? [this.props.target.AdGroupId] : [],
    }];

    const userAction = isApply ? APPLY_ACTION_TYPES.ACCEPT : APPLY_ACTION_TYPES.DISMISS;
    const userInputs = this.getUserInputs(isApply);

    return {
      userInputs,
      userAction,
      type: this.props.type,
      campaignAdGroups,
      channel: CHANNEL_TYPES.COMPETITION,
      context: this.props.context,
      guid: this.props.guid,
    };
  }

  apply = () => {
    this.props.logExpInfo('1 recommendation applied');
    const ops = this.buildRequestOptions(true);
    this.props.track(ops);
  }

  dismiss = () => {
    const ops = this.buildRequestOptions(false);
    this.props.track(ops);
  }

  isTheRowCurrent(rowIndex) {
    return this.props.hasLastRowAsCurrent &&
      rowIndex === this.props.bodies.length - 1;
  }

  generateInputId(index) {
    return `${this.props.name}_${index}`;
  }

  buildHeaders(hidenIndexes, end = this.props.headers.length) {
    const { headers, helpTopics, singleSelection } = this.props;
    const headersElements = [];
    _.each(
      headers.slice(0, end),
      (headerText, index) => {
        if (_.contains(hidenIndexes, index)) {
          return;
        }
        const originalIndex = index;
        const help = helpTopics[originalIndex] ?
          (<HelpPopup topic={helpTopics[originalIndex]} />) : null;
        let header = null;

        if (originalIndex !== 0 || singleSelection) {
          header = headerText;
        } else {
          const inputId = this.generateInputId(-1);
          header = (
            <Checkbox
              checked={_.every(this.state.rowState)}
              onChange={this.handleCheckboxChange}
              id={inputId}
              name="-1"
            >
              {headerText}
            </Checkbox>);
        }
        headersElements.push(<div key={index} className={`header form-action-col-${headersElements.length + 1}${singleSelection ? ' radio-header' : ''}`}>{header}{help}</div>);
      }
    );

    return headersElements;
  }

  buildActionPanel() {
    const {
      title, bodies, i18n, singleSelection, columns, children, readOnly,
    } = this.props;
    const currentString = `(${i18n.getString('Details_View_Current')})`;

    const bodiesElements = _.map(bodies, (row, rowIndex) => {
      const config = columns[0];
      let input = null;
      let value = formatIncreasedValue(row[0], config.isIncrease);
      let className = 'cell';

      const inputId = this.generateInputId(rowIndex);

      if (this.isTheRowCurrent(rowIndex)) {
        value = `${value} ${currentString}`;
      } else if (singleSelection) {
        input = (<input
          type="radio"
          checked={rowIndex.toString() === this.state.selectedRow}
          onChange={this.handleRadioChange}
          value={rowIndex}
          id={inputId}
        />);
        className += ' radio';
      } else {
        input = (
          <Checkbox
            checked={this.state.rowState[rowIndex]}
            onChange={this.handleCheckboxChange}
            id={inputId}
            name={rowIndex.toString()}
          />);
        className += ' checkbox';
      }

      return (
        <div key={rowIndex} className={`${className} grid-action-estimates-row-${rowIndex + 2}`}>
          {input ? (
            <label htmlFor={inputId}>
              {input}{value}
            </label>) : <span>{value}</span>
          }
        </div>
      );
    });

    const disabled = readOnly || (!singleSelection && !_.some(this.state.rowState));

    return (
      <GridPanel
        title={title}
        titleForm={children}
        headers={this.buildHeaders(null, 1)}
        bodies={bodiesElements}
      >
        <button type="button" className="btn btn-primary" onClick={this.apply} disabled={disabled}>
          {i18n.getString('Details_View_Apply')}
        </button>
        <button type="button" className="btn btn-default" onClick={this.dismiss} disabled={disabled}>
          {i18n.getString('Details_View_Dismiss')}
        </button>
      </GridPanel>);
  }

  buildEstimatesGridCell(rowIndex, colIndex, cellContent, config) {
    const isCurrentRow = this.isTheRowCurrent(rowIndex);

    let icon = null;
    if (config.enableCheckedIcon && this.props.singleSelection) {
      const hasIcon = rowIndex >= this.state.selectedRow;
      icon = <span className={`icon-left glyphicon ${hasIcon ? 'glyphicon-ok' : ''}`} />;
    }
    const cell = (
      <div key={colIndex} className={`cell grid-action-estimates-row-${rowIndex + 2} form-action-col-${colIndex}`}>
        <span>
          {cellContent && icon}
          {formatIncreasedValue(cellContent, config.isIncrease && !isCurrentRow)}
        </span>
      </div>);

    return cell;
  }

  buildEstimatesPanel() {
    // Set auto hide columns
    const configs = this.props.columns;
    const hidenIndexes = [0];
    _.each(configs, (config, index) => {
      if (config && config.autoHide && _.every(this.props.bodies, body => body[index] === '0')) {
        hidenIndexes.push(index);
      }
    });

    const title = this.props.i18n.getString('Details_View_Weekly_Estimates');
    const bodiesElements = _.map(this.props.bodies, (row, rowIndex) => {
      const cells = [];
      _.each(row, (cell, col) => {
        if (!_.contains(hidenIndexes, col)) {
          cells.push(this.buildEstimatesGridCell(rowIndex, cells.length + 1, cell, configs[col]));
        }
      });
      return (
        <React.Fragment key={rowIndex}>
          {cells}
        </React.Fragment>
      );
    });

    return (
      <GridPanel
        title={title}
        headers={this.buildHeaders(hidenIndexes)}
        bodies={bodiesElements}
        titleForm={this.props.children && <div />} // this is for align action and estimates tables
      />);
  }

  render() {
    const { rowClass } = this.props;
    return (
      <React.Fragment>
        <div className={`details-action-col ${rowClass}`}>
          {this.buildActionPanel()}
        </div>
        <div className={`details-estimates-col ${rowClass}`}>
          {this.buildEstimatesPanel()}
        </div>
      </React.Fragment>
    );
  }
}
